import {useEffect, useState, useCallback} from 'react';
import {Link} from 'react-router-dom';
import {Button, Spin, Table, Popconfirm, message, Space} from 'antd';
import {DeleteOutlined} from '@ant-design/icons';

import {Test} from '../../graphql/API';
import {TestsService} from './TestsService';
import {DailyExerciseSearch} from './DailyExerciseSearch';
import Title from 'antd/es/typography/Title';

const testsService = new TestsService();

export const TestsTable = () => {
  const [tests, setTests] = useState<Array<Test> | []>([]);
  const [testsSearchResult, setTestsSearchResult] = useState<Array<Test> | []>(
    [],
  );
  const [isError, setIsError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Search state management
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [searchPagination, setSearchPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  // OLD CODE - Commented out (old token-based pagination)
  // const [pagination, setPagination] = useState({
  //   current: 1,
  //   pageSize: 50,
  //   total: 0,
  //   nextToken: null,
  // });

  // NEW CODE - Page-based pagination for daily exercises (20 per page)
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  // OLD CODE - Commented out (old token-based data fetching)
  // useEffect(() => {
  //   testsService
  //     .getTests(null, pagination.pageSize)
  //     .then((resp) => {
  //       setPagination({
  //         ...pagination, // Spread the existing pagination state
  //         nextToken: resp.nextToken, // Update the nextToken
  //         total: resp.scannedCount + pagination.pageSize,
  //       });

  //       setIsLoading(false);
  //       setTests(resp.items);
  //     })
  //     .catch((error) => {
  //       setIsError(true);
  //       console.log('Error ->', error);
  //     });
  // }, []);

  // NEW CODE - Fetch daily exercises with page-based pagination
  useEffect(() => {
    testsService
      .getTests(1, 20) // Start with page 1
      .then(resp => {
        setPagination({
          current: resp.page,
          pageSize: 20,
          total: resp.total,
        });

        setIsLoading(false);
        setTests(resp.items);
      })
      .catch(error => {
        setIsError(true);
        console.log('Error fetching daily exercises ->', error);
      });
  }, []);

  // OLD CODE - Commented out (old token-based pagination logic)
  // const getNextTests = async (
  //   nextToken: string | null,
  //   count: number,
  //   nextPage: number,
  // ) => {
  //   if (pagination.nextToken !== null) {
  //     testsService
  //       .getTests(nextToken, count)
  //       .then((resp) => {
  //         setPagination({
  //           ...pagination, // Spread the existing pagination state
  //           nextToken: resp.nextToken, // Update the nextToken
  //           total: resp.nextToken
  //             ? pagination.total + pagination.pageSize
  //             : pagination.total,
  //           current: nextPage,
  //         });
  //         let newTestsList = tests.concat(resp.items);
  //         setIsLoading(false);
  //         setTests(newTestsList);
  //       })
  //       .catch((error) => {
  //         setIsError(true);
  //         console.log('Error ->', error);
  //       });
  //   } else {
  //     setPagination({
  //       ...pagination, // Spread the existing pagination state
  //       current: nextPage,
  //     });
  //   }
  // };

  // NEW CODE - Page-based pagination for daily exercises
  const getNextTests = async (nextPage: number) => {
    setIsLoading(true);
    testsService
      .getTests(nextPage, 20)
      .then(resp => {
        setPagination({
          current: resp.page,
          pageSize: 20,
          total: resp.total,
        });
        setIsLoading(false);
        setTests(resp.items);
      })
      .catch(error => {
        setIsError(true);
        setIsLoading(false);
        console.log('Error fetching daily exercises ->', error);
      });
  };

  // OLD CODE - Commented out (old token-based table change handler)
  // const handleTableChange = async (pagination: any, filters, sorter) => {
  //   await getNextTests(pagination.nextToken, 50, pagination.current);
  // };

  // NEW CODE - Page-based table change handler (handles both regular and search pagination)
  const handleTableChange = async (pagination: any, filters, sorter) => {
    if (searchTerm) {
      // If searching, handle search pagination
      await handleSearchPagination(pagination.current);
    } else {
      // Otherwise, handle regular pagination
      await getNextTests(pagination.current);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      setIsLoading(true);
      await testsService.deleteTest(id);
      message.success('Daily exercise deleted successfully');
      // Refresh the table data - use search if active
      if (searchTerm) {
        await handleSearch(searchTerm);
        setIsLoading(false);
      } else {
        await getNextTests(pagination.current);
      }
    } catch (error) {
      message.error('Failed to delete daily exercise');
      console.error('Delete error:', error);
      setIsLoading(false);
    }
  };

  // NEW CODE - Handle search with new API
  const handleSearch = useCallback(async (term: string) => {
    if (term.length >= 3) {
      setIsSearching(true);
      setSearchTerm(term);
      try {
        const result = await testsService.searchTestsByName(term, 1, 20);
        setTestsSearchResult(result.items);
        setSearchPagination({
          current: result.page,
          pageSize: 20,
          total: result.total,
        });
      } catch (error) {
        console.error('Search error:', error);
        message.error('Failed to search daily exercises');
      } finally {
        setIsSearching(false);
      }
    }
  }, []); // Empty deps - testsService is stable, all state updates are handled

  // NEW CODE - Handle search clear
  const handleClearSearch = useCallback(() => {
    setSearchTerm('');
    setTestsSearchResult([]);
    setSearchPagination({
      current: 1,
      pageSize: 20,
      total: 0,
    });
  }, []);

  // NEW CODE - Handle search pagination
  const handleSearchPagination = async (page: number) => {
    if (searchTerm) {
      setIsSearching(true);
      try {
        const result = await testsService.searchTestsByName(
          searchTerm,
          page,
          20,
        );
        setTestsSearchResult(result.items);
        setSearchPagination({
          current: result.page,
          pageSize: 20,
          total: result.total,
        });
      } catch (error) {
        console.error('Search pagination error:', error);
        message.error('Failed to load search results');
      } finally {
        setIsSearching(false);
      }
    }
  };

  const columns: any = [
    {
      title: 'ID',
      dataIndex: 'id',
      sorter: (a: any, b: any) => a.id.length - b.id.length,
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (id: string) => <Link to={'/tests/' + id}>{id}</Link>,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      sorter: (a: any, b: any) => {
        return a?.name.localeCompare(b?.name);
      },
      sortDirections: ['ascend', 'descend', 'ascend'],
    },
    {
      title: 'Type',
      dataIndex: 'type',
      sorter: (a: any, b: any) => {
        return a?.type.localeCompare(b?.type);
      },
      sortDirections: ['ascend', 'descend', 'ascend'],
    },
    {
      title: 'Created at',
      dataIndex: 'createdAt',
      defaultSortOrder: 'descend',
      sorter: (a: any, b: any) => {
        return a?.createdAt.localeCompare(b?.createdAt);
      },
      sortDirections: ['ascend', 'descend', 'ascend'],
    },
    {
      title: 'Updated at',
      dataIndex: 'updatedAt',
      defaultSortOrder: 'descend',
      sorter: (a: any, b: any) => {
        return a?.updatedAt.localeCompare(b?.updatedAt);
      },
      sortDirections: ['ascend', 'descend', 'ascend'],
    },
    {
      title: 'Actions',
      key: 'actions',
      dataIndex: 'id',
      render: (id: string) => (
        <Space>
          <Link to={'/test/' + id + '/edit'}>
            <Button>Edit</Button>
          </Link>
          {/* <Popconfirm
            title="Are you sure you want to delete this daily exercise?"
            okText="Yes"
            cancelText="No"
            onConfirm={() => handleDelete(id)}>
            <Button danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm> */}
        </Space>
      ),
    },
  ];

  return (
    <>
      <Button size={'large'} style={{float: 'right', margin: 20}}>
        <Link to={'/tests/create'}>Create</Link>
      </Button>
      {!isError && !isLoading && (
        <>
          <DailyExerciseSearch
            onSearch={handleSearch}
            onClear={handleClearSearch}
            isLoading={isSearching}
          />
          <Table
            pagination={searchTerm ? searchPagination : pagination}
            columns={columns}
            dataSource={
              testsSearchResult.length !== 0
                ? testsSearchResult?.map(test => ({...test, key: test.id}))
                : tests?.map(test => ({...test, key: test.id}))
            }
            rowKey={record => record.id}
            onChange={handleTableChange}
            loading={isSearching}
          />
        </>
      )}
      {isLoading && (
        <Spin size="large" style={{margin: 'auto', width: '100%'}} />
      )}
      {isError && <Title>Daily Exercises fetching error ⚠️</Title>}
    </>
  );
};
